import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { X, Copy, Check, Code2 } from "lucide-react";
import { toast } from "sonner";
import { copyToClipboard } from "@/lib/apiClient";

export default function EmbedModal({ title, description, path, height = 640, onClose }) {
  const [copied, setCopied] = useState(false);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const fid = useMemo(() => "hirescope-" + Math.random().toString(36).slice(2, 9), []);
  const src = `${origin}${path}${path.includes("?") ? "&" : "?"}fid=${fid}`;
  const code = `<iframe id="${fid}" src="${src}" width="100%" height="150" style="border:none;width:100%;" frameborder="0" scrolling="no" title="${title}"></iframe>
<script>window.addEventListener("message",function(e){var d=e.data||{};if(d.type==="hirescope-embed-height"&&d.id==="${fid}"){var f=document.getElementById("${fid}");if(f){f.style.height=d.height+"px";}}});</script>`;

  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const copy = async () => {
    const ok = await copyToClipboard(code);
    if (ok) {
      setCopied(true);
      toast.success("Embed code copied");
      setTimeout(() => setCopied(false), 1800);
    } else {
      toast.error("Couldn't copy automatically — select the code and copy it manually");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm px-5" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl shadow-xl border border-black/5 p-7 w-full max-w-lg" data-testid="embed-modal">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-xl font-extrabold tracking-tight flex items-center gap-2"><Code2 size={20} className="text-[#007AFF]" /> {title}</h2>
          <button onClick={onClose} className="text-[#86868B] hover:text-[#1D1D1F]"><X size={20} /></button>
        </div>
        {description && <p className="text-sm text-[#86868B] mb-5">{description}</p>}

        <label className="text-xs font-bold uppercase tracking-wide text-[#86868B]">Embed code</label>
        <div className="mt-1.5 relative">
          <textarea readOnly value={code} data-testid="embed-code" rows={6}
            className="w-full bg-[#1D1D1F] text-[#E5E5EA] font-mono text-xs rounded-2xl p-4 pr-4 outline-none resize-none leading-relaxed scrollbar-thin" />
        </div>
        <button onClick={copy} data-testid="copy-embed-code"
          className="mt-3 w-full bg-[#007AFF] text-white rounded-full px-6 py-3 font-semibold text-sm hover:bg-[#0051FF] active:scale-[0.98] transition-all flex items-center justify-center gap-2">
          {copied ? <><Check size={16} /> Copied</> : <><Copy size={16} /> Copy embed code</>}
        </button>
        <p className="text-xs text-[#86868B] mt-3">Paste this into any website's HTML. Only the job listings are shown, and the box auto-fits its height — no inner scrollbar. Clicking a role opens the application form in a new tab.</p>
      </motion.div>
    </div>
  );
}
